'use client';

import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { OrganizationModel, ProjectModel } from '@/models/organizations/internal-models';
import { useFetchOrganizations, useFetchSavedFilters } from '@/models/organizations/useFetchOrganizations';
import { useQueryParamStateV2 } from '@/utils/use-query-param-state';
import {
  PARAMS_KEY_ORGANIZATION_ID,
  PARAMS_KEY_PROJECT_ID,
  PARAMS_KEY_REPORT_ID,
  PARAMS_KEY_REPORT_SIDEBAR_SECTION,
} from '@/constants/route-params-keys';
import { useLocalStorageState } from '@/hooks/useSessionState';
import { ProjectSelection } from '@/domains/dashboard/components/ProjectSelect';
import { useAuthContext } from '@/hooks/use-auth';
import { AggregateBy } from '@/domains/dashboard/components/GlobalfiltersRow';
import {
  CategoryDescription,
  FilterGroup,
  GetInteractionAggregatesReqFiltersItem,
  AiSettings,
  CustomEntitySetting,
  TagSetting,
  TypeOfProblemSettingsUnhandledRequestsAnyOfItem,
  TypeOfProblemSettingsEmptyResponseAnyOfItem,
  TypeOfProblemSettingsLanguageProblemsAnyOfItem,
  TypeOfProblemSettingsOffTopicAnyOfItem,
  TypeOfProblemSettingsTaskFailureAnyOfItem,
  TypeOfProblemSettingsUserFrustrationAnyOfItem,
  TypeOfProblemSettingsThumbsDownAnyOfItem,
} from '@/api/generated/model';
import annotationPlugin from 'chartjs-plugin-annotation';
import { useGlobalFiltersStore } from '@/contexts/filters/filter-context';
import {
  BookOpen,
  Bug,
  CircleQuestionMark,
  Construction,
  Info,
  Languages,
  MessageCircleQuestionMark,
  OctagonAlert,
  PhoneMissed,
  Radiation,
  ShieldMinus,
  ThumbsDown,
} from 'lucide-react';
import { capitalize } from 'lodash';
import { IconType } from '@/constants/icons';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEY_PROJECT_CONFIG } from '@/constants/query-keys';
import { getConfiguration as getProjectConfiguration } from '@/api/generated/projects/projects';

// NOTE: This is a temporary solution to fix the issue with Chartjs
if (typeof window !== 'undefined') {
  (async () => {
    const { default: zoomPlugin } = await import('chartjs-plugin-zoom');
    ChartJS.register(zoomPlugin);
  })();
}
ChartJS.register(...registerables, ChartDataLabels, annotationPlugin);

export type ProjectsContextState = {
  selectedProject?: ProjectModel;
  setProjectId: (ProjectModel: string | null) => void;
  /** Clears in-memory chart filters only (does not touch URL — avoid nuqs updates on /explore before navigating away). */
  clearChartFilters: () => void;
  projects: ProjectModel[];
  defaultAggregateBy: AggregateBy;
  isLoadingProjects: boolean;
  selectedOrganization: OrganizationModel | undefined;
  organizationId: string | undefined;
  setOrganizationId: (organizationId: string | undefined) => void;
  organizations: OrganizationModel[];
  projectId?: string;
  isMappedProject: boolean;
  savedFilters: FilterGroup[];
  failureCategories: FailureCategory[];
};
type FailureCategory = {
  value: string;
  title: string;
  description: string;
  icon: IconType;
  source: 'default' | 'ai_settings';
};

export const ProjectsContext = createContext<ProjectsContextState | null>(null);

export function ProjectsProvider({ children }: PropsWithChildren) {
  const { tenant, setAsTenant, isNebulyAdmin, loading: isAuthLoading } = useAuthContext();
  const { data: organizationsData, isLoading } = useFetchOrganizations();
  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationModel | undefined>(undefined);
  const [defaultAggregateBy, setDefaultAggregateBy] = useState<AggregateBy>('interaction');
  const { setFiltersStore } = useGlobalFiltersStore<GetInteractionAggregatesReqFiltersItem>();

  const [organizationId, setOrganizationId] = useQueryParamStateV2<string | undefined>(
    PARAMS_KEY_ORGANIZATION_ID,
    undefined,
    {
      type: 'string',
    },
  );
  const [_projectId, _setProjectId] = useQueryParamStateV2<string | null>(PARAMS_KEY_PROJECT_ID, null, {
    type: 'string',
  });

  const [, _setReportId] = useQueryParamStateV2(PARAMS_KEY_REPORT_ID, '', {
    type: 'string',
    pushOrReplace: 'replace',
  });
  const [, setReportSidebarSection] = useQueryParamStateV2<string | null>(PARAMS_KEY_REPORT_SIDEBAR_SECTION, null, {
    type: 'string',
    pushOrReplace: 'replace',
  });

  // Get project configuration for AI settings
  const { data: projectConfigData } = useQuery({
    queryKey: [QUERY_KEY_PROJECT_CONFIG, _projectId],
    queryFn: ({ signal }) => getProjectConfiguration({ project_id: _projectId! }, { signal }),
    enabled: !!_projectId,
    staleTime: 0,
  });

  const projectConfig = projectConfigData?.configuration;

  const setProjectId = useCallback(
    (newProjectId: string | null) => {
      if (newProjectId === _projectId) return;
      setFiltersStore({});
      _setProjectId(newProjectId);
      _setReportId('');
      setReportSidebarSection(null);
    },
    [_projectId, _setProjectId, setFiltersStore, _setReportId, setReportSidebarSection],
  );

  const clearChartFilters = useCallback(() => {
    setFiltersStore({});
  }, [setFiltersStore]);

  const [lastSelectedProjects, setLastSelectedProjects] = useLocalStorageState<ProjectSelection | null>(
    'lastSelectedProjects',
    null,
  );

  const projects = useMemo(() => selectedOrganization?.projects || [], [selectedOrganization]);

  const selectedProject = useMemo(() => {
    return projects.find((project) => project.id === _projectId);
  }, [projects, _projectId]);

  const { data: savedFilters } = useFetchSavedFilters(selectedProject?.id);
  const switchingTenantRef = useRef(false);

  useEffect(() => {
    if (switchingTenantRef.current) return;
    const organizations = organizationsData?.organizations;
    if (!organizations?.length || isAuthLoading) return;
    const orgId = organizationId ?? lastSelectedProjects?.organization_id;
    const projId = _projectId ?? lastSelectedProjects?.project_id;
    const shouldSwitchTenant = lastSelectedProjects?.tenant !== tenant;

    if (isNebulyAdmin && lastSelectedProjects?.tenant && shouldSwitchTenant) {
      switchingTenantRef.current = true;
      setAsTenant(lastSelectedProjects?.tenant);
      window.location.reload();
      return;
    }

    // auto select the first organization if not selected or the selected one is not found
    if (!orgId || !organizations.find((org) => org.id === orgId)) {
      setSelectedOrganization(organizations[0]);
      setOrganizationId(organizations[0].id);
      setDefaultAggregateBy(organizations[0].defaultAggregateBy);

      // auto select the first project
      if (organizations[0].projects.length && !projId) {
        setLastSelectedProjects({
          organization_id: organizations[0].id,
          project_id: organizations[0].projects[0].id,
          tenant: tenant,
        });
        setProjectId(organizations[0].projects[0].id);
      }
    } else {
      setOrganizationId(orgId);
      const orgFound = organizations.find((org) => org.id === orgId);

      if (!orgFound) {
        setSelectedOrganization(organizations[0]);
        setDefaultAggregateBy(organizations[0].defaultAggregateBy);
      } else {
        // if an organization is found in the query params, select it,
        // this is to handle the case where the user RELOAD the page
        // and want to keep a previously selected organization
        setSelectedOrganization(orgFound);
        setDefaultAggregateBy(orgFound.defaultAggregateBy);
      }

      // setSelectedOrganization(orgFound);

      // auto select the project from query params or the first project
      const projects = organizations.find((org) => org.id === orgId)?.projects || [];
      const targetProject = projects.find((project) => project.id === projId);
      if (projects.length > 0) {
        setProjectId(targetProject?.id || projects[0].id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(organizationsData), organizationId, isNebulyAdmin, isAuthLoading]);

  const isMappedProject = useMemo(() => {
    return selectedProject?.mappedProjectId !== undefined && selectedProject?.mappedProjectId !== null;
  }, [selectedProject]);

  const failureCategories = useMemo(() => {
    if (!organizationsData || !selectedProject) return [];

    // Get default failure categories
    const defaultCategories =
      selectedProject.failureCategories?.map((cat) => buildFailureCategory(cat, 'default')) ??
      organizationsData.defaultFailureCategories.map((cat) => buildFailureCategory(cat, 'default'));

    // Get AI settings categories (non-automatic ones)
    const aiSettingsCategories = getAiSettingsFailureCategories(projectConfig?.ai_settings);

    // Combine and deduplicate categories
    const allCategories = [...defaultCategories, ...aiSettingsCategories];
    const uniqueCategories = new Map<string, FailureCategory>();

    allCategories.forEach((category) => {
      if (!uniqueCategories.has(category.value)) {
        uniqueCategories.set(category.value, category);
      }
    });

    return Array.from(uniqueCategories.values());
  }, [organizationsData, selectedProject, projectConfig?.ai_settings]);

  const taxonomyAliases = useMemo(() => {
    return projectConfig?.taxonomy_aliases ?? {};
  }, [projectConfig]);

  const value: ProjectsContextState = useMemo(
    () => ({
      selectedProject,
      projects,
      isLoadingProjects: isLoading,
      setProjectId,
      clearChartFilters,
      organizationId,
      setOrganizationId,
      selectedOrganization,
      failureCategories,
      organizations: organizationsData?.organizations || [],
      projectId: selectedProject?.id ?? undefined,
      isMappedProject,
      defaultAggregateBy,
      savedFilters: savedFilters || [],
      taxonomyAliases,
    }),
    [
      isLoading,
      organizationId,
      organizationsData,
      projects,
      selectedOrganization,
      selectedProject,
      setOrganizationId,
      setProjectId,
      clearChartFilters,
      isMappedProject,
      defaultAggregateBy,
      savedFilters,
      failureCategories,
      taxonomyAliases,
    ],
  );
  return <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>;
}

export const useProjectsContext = () => {
  const context = useContext(ProjectsContext);

  if (!context) throw new Error('useProjectsContext context must be use inside ProjectsContextProvider');

  return context;
};

function getAiSettingsFailureCategories(aiSettings: AiSettings | undefined): FailureCategory[] {
  if (!aiSettings?.type_of_problems) return [];

  const categories: FailureCategory[] = [];
  const problemSettings = aiSettings.type_of_problems;

  // Map of AI settings keys to their display names
  const problemKeyMap: Record<string, string> = {
    unhandled_requests: 'unhandled requests',
    empty_response: 'empty response',
    language_problems: 'language problems',
    out_of_scope: 'out of scope',
    task_failure: 'task failure',
    off_topic: 'off-topic',
    user_frustration: 'user frustration',
    thumbs_down: 'thumbs down',
  };

  // Check each problem type setting
  Object.entries(problemKeyMap).forEach(([key, displayName]) => {
    let setting:
      | TagSetting
      | string
      | TypeOfProblemSettingsUnhandledRequestsAnyOfItem[]
      | TypeOfProblemSettingsEmptyResponseAnyOfItem[]
      | TypeOfProblemSettingsLanguageProblemsAnyOfItem[]
      | TypeOfProblemSettingsOffTopicAnyOfItem[]
      | TypeOfProblemSettingsTaskFailureAnyOfItem[]
      | TypeOfProblemSettingsUserFrustrationAnyOfItem[]
      | TypeOfProblemSettingsThumbsDownAnyOfItem[]
      | undefined = undefined;

    switch (key) {
      case 'unhandled_requests':
        setting = problemSettings.unhandled_requests;
        break;
      case 'empty_response':
        setting = problemSettings.empty_response;
        break;
      case 'language_problems':
        setting = problemSettings.language_problems;
        break;
      case 'off_topic':
        setting = problemSettings.off_topic;
        break;
      case 'task_failure':
        setting = problemSettings.task_failure;
        break;
      case 'user_frustration':
        setting = problemSettings.user_frustration;
        break;
      case 'thumbs_down':
        setting = problemSettings.thumbs_down;
        break;
    }

    if (setting && setting !== 'automatic' && !Array.isArray(setting)) {
      // This is a non-automatic setting (TagSetting or string), add it as a category
      categories.push({
        value: displayName,
        title: capitalize(displayName),
        description: `AI-managed category: ${displayName}`,
        icon: iconForFailureCategory(displayName),
        source: 'ai_settings',
      });
    } else if (setting && Array.isArray(setting) && setting.length > 0) {
      // This is a filters array (non-automatic), add it as a category
      categories.push({
        value: displayName,
        title: capitalize(displayName),
        description: `AI-managed category: ${displayName}`,
        icon: iconForFailureCategory(displayName),
        source: 'ai_settings',
      });
    }
  });

  // Add custom entities
  if (problemSettings.custom_entities) {
    problemSettings.custom_entities.forEach((entity: CustomEntitySetting) => {
      categories.push({
        value: entity.name,
        title: capitalize(entity.name),
        description: entity.description || `Custom category: ${entity.name}`,
        icon: iconForFailureCategory(entity.name),
        source: 'ai_settings',
      });
    });
  }

  return categories;
}

function buildFailureCategory(
  failureCategory: CategoryDescription,
  source: 'default' | 'ai_settings' = 'default',
): FailureCategory {
  return {
    value: failureCategory.name,
    title: capitalize(failureCategory.name),
    description: failureCategory.description,
    icon: iconForFailureCategory(failureCategory.name),
    source,
  };
}

function iconForFailureCategory(failureCategory: string): IconType {
  switch (failureCategory) {
    // default classes
    case 'unhandled requests':
    case 'unable to perform requested action':
      return PhoneMissed;
    case 'empty response':
      return ShieldMinus;
    case 'language mismatch':
    case 'language problems':
    case 'wrong language':
      return Languages;
    case 'out of scope':
      return Construction;
    case 'technical error':
      return Bug;
    // lottoland_voc
    case 'lack of information':
      return BookOpen;
    case 'wrong response':
      return Radiation;
    case 'misunderstanding':
      return MessageCircleQuestionMark;
    case 'ai misbehavior':
      return OctagonAlert;
    case 'other':
      return CircleQuestionMark;
    case 'thumbs down':
      return ThumbsDown;
    default:
      return Info;
  }
}
